import './org-hac-profile.scss';
import React, {useEffect, useState} from 'react';
import {Navigate, useLocation, useNavigate, useParams} from 'react-router-dom';
import Header from '../../header/header.js';
import RoleRibbonByRole from '../role-ribbon/role-ribbon-by-role';
import decodeToken from '../../../../utils/decode-token.js';
import Grid from '@mui/material/Grid2';

import {Controller, useForm} from 'react-hook-form';
import Joi from 'joi';
import Input from '../../../atoms/input.js';
import Snackbar from '@mui/material/Snackbar';
import {useTranslation} from 'react-i18next';
import SafeHtml from '../../../atoms/safe-html';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {DateField} from '@mui/x-date-pickers/DateField';
import dayjs from 'dayjs';
import {Alert, Collapse} from '@mui/material';
import {parsePhoneNumberFromString} from 'libphonenumber-js';
import {PhoneInput} from 'react-international-phone';
import 'react-international-phone/style.css';
import {deleteAddress, getAcademicTitles, getCountries, getHacData, updateHac, updateSvId} from '../../../../api/hacApi.js';
import {HAC_STATUS} from '../../../../constants/constants.js';
import ModalResendNomination from '../../../organisms/role-retailer/modal-resend-nomination/modal-resend-nomination';
import HacNavigation from '../hac-navigation/hac-navigation';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import {
  canAccessHacManagement,
  getHacBackPathForRole,
  isAdminRole,
  isPmaRole,
  isRetailerRole,
} from '../../../../utils/role-utils.js';
import HacAddressSection from './hac-address-section.js';
import BackButton from '../../../atoms/back-button';
import {
  buildAddressPayload,
  hasAnyAddressFieldFilled,
} from './hac-address-utils.js';

const setValues = (values, setterFn) => {
  const requiredValues = [
    'name',
    'email',
    'middle_name',
    'last_name',
    'phone',
    'preferred_address',
    'preferred_language',
    'prefix_phone',
    'title',
    'academic_title',
    'birth_day',
    'birth_day_partner',
    'pma_notes',
    'name_partner',
    'last_name_partner',
    'id_hac'
  ];
  for (const [key, value] of Object.entries(values)) {
    if (requiredValues.includes(key) && value) {
      setterFn(key, value);
    }
  }
};

function EditHacProfile() {
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;

  const navigate = useNavigate();
  const {t} = useTranslation();

  const {hacId} = useParams();

  const [countries, setCountries] = useState([]);
  const [areaCode, setAreaCode] = useState('');
  const [academicTitles, setAcademicTitles] = useState([]);
  const [hacData, setHacData] = useState({});
  const [profileStatus, setProfileStatus] = useState(HAC_STATUS.Nominated);
    const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [trackingLink, setTrackingLink] = useState(null);
  const trackingText = trackingLink
    ? t('common-shipping-link').replace('{trackingLink}', trackingLink)
    : '';
  const [svState, setSvState] = React.useState('not_searched');
  const [svAccountId, setSvAccountId] = React.useState('');
  const [svSaving, setSvSaving] = useState(false);
  const [languages, setLanguages] = useState([]);

  const handleOpenResendModal = (event) => {
    event.preventDefault();
    setResendModalOpen(true);
  };

  const handleCloseResendModal = (event) => {
    setResendModalOpen(false);
  };

  const location = useLocation();
  const {breadcrumbName, backPath} = location.state || {};
  const navigationQuery = new URLSearchParams(location.search);
  const requestedReturnTo = navigationQuery.get('returnTo');
  const queryReturnTo = requestedReturnTo?.startsWith('/') && !requestedReturnTo.startsWith('//')
    ? requestedReturnTo
    : null;
  const origin = location.state?.origin || navigationQuery.get('origin');
  const isAdmin = isAdminRole(role);
  const isPma = isPmaRole(role);
  const isRetailer = isRetailerRole(role);
  const canShowTrackingLink = canAccessHacManagement(role);
  const canResendNomination = isRetailer || isAdmin;


  const {
    formState,
    register,
    handleSubmit,
    setValue,
    watch,
    resetField,
    setFocus,
    control,
  } = useForm();

  const [preferredAddress, setPreferredAddress] = useState('');

  const privateAddress = watch('private_address');
  const companyAddress = watch('company_address');
  const selectedLanguage = watch('preferred_language');

  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    address: true,
  });

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  const {errors} = formState;

  useEffect(() => {
    const fetchAcademicTitles = async () => {
      if (areaCode) {
        try {
          const response = await getAcademicTitles(token, areaCode);
          setAcademicTitles(response.data);
        } catch (error) {
          if (error.response) {
            setSnackbarOpen(true);
            setSnackbarSeverity('error');
            setSnackbarMessage(t('common-internal-server-error'), error);
          }
        }
      }
    };

    fetchAcademicTitles();
  }, [token, t, areaCode]);

  useEffect(() => {
    if (hacData.academic_title) {
      setValue('academic_title', hacData.academic_title);
    }
  }, [hacData.academic_title, setValue]);

  useEffect(() => {
    const fetchCountries = async () => {
      const countries = await getCountries();
      setCountries(countries.data);
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (!countries.length || !areaCode) {
      setLanguages([]);
      setValue('preferred_language', hacData?.preferred_language || '');
      return;
    }

    const country = countries.find((item) => item.area_code === areaCode);

    if (!country) {
      setLanguages([]);
      setValue('preferred_language', hacData?.preferred_language || '');
      return;
    }

    const availableLanguages = (country.language || []);

    setLanguages(availableLanguages);

    const currentLanguage = hacData?.preferred_language || '';

    if (currentLanguage && availableLanguages.includes(currentLanguage)) {
      setValue('preferred_language', currentLanguage);
    } else {
      setValue('preferred_language', availableLanguages[0] || '');
    }
  }, [countries, areaCode, hacData, setValue]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getHacData(token, hacId);

        setProfileStatus(response.data.status);
        if (response.status !== 200) throw new Error('Error getting hac data');
        setProfileStatus(response.data.status);
        setPreferredAddress(response.data.preferred_address);
        setValues(response.data, setValue);
        if (response.data.prefix_phone && response.data.phone) {
          setPhone(`${response.data.prefix_phone}${response.data.phone}`);
        }
        setAreaCode(response.data.area_code);
        setHacData(response.data);

        const incomingSv = response.data?.sv_account_id;

        if (!incomingSv) {
          // no llega sv_account_id
          setSvState('not_searched');
          setSvAccountId('');
        } else if (String(incomingSv) === 'not_found') {
          // llega "not_found"
          setSvState('id_not_found');
          setSvAccountId('not_found');
        } else {
          // llega algo distinto de "not_found"
          setSvState('id_added');
          setSvAccountId(String(incomingSv));
        }

        // y para que el form tenga el valor
        setValue('sv_account_id', incomingSv && String(incomingSv) !== 'not_found' ? String(incomingSv) : (incomingSv ? 'not_found' : ''));

        setTrackingLink(response.data.url_tracking_welcome_pack || null);

        const privateAddr = response.data.address.find(
          (address) => address.address_type === 'private'
        );
        const companyAddr = response.data.address.find(
          (address) => address.address_type === 'company'
        );
        if (companyAddr) {
          setValue('company_address', companyAddr);
        }
        if (privateAddr) {
          setValue('private_address', privateAddr);
        }
      } catch (error) {
        if (error.response) {
          setSnackbarOpen(true);
          setSnackbarSeverity('error');
          setSnackbarMessage(t('common-internal-server-error'), error);
        }
      }
    };
    fetchData();
  }, [t, token, hacId, setValue]);

  const handlePreferredAddressChange = (event) => {
    setPreferredAddress(event.target.value);
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClearAddress = async (type) => {
    const address = type === 'private' ? privateAddress : companyAddress;

    try {
      await deleteAddress(token, address.hac, address.id);
      Object.keys(address).forEach((key) => {
        address[key] = undefined;
        resetField(`${type}_address.${key}`);
      });

      setSnackbarMessage(t('alert-address-deleted'));
      setSnackbarSeverity('success');
    } catch (error) {
      setSnackbarMessage(
        error.response?.status === 409
          ? t('alert-address-delete-preferred')
          : t('common-internal-server-error')
      );
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const onSubmit = handleSubmit(
    async ({
             private_address,
             company_address,
             middle_name,
             birth_day,
             birth_day_partner,
             name_partner,
             last_name_partner,
             ...rest
           }) => {
      if (Object.keys(errors).length) return;
      let payload = {
        ...rest,
        middle_name: middle_name.length ? middle_name : undefined,
        name_partner: name_partner
          ? name_partner.length
            ? name_partner
            : undefined
          : undefined,
        last_name_partner: last_name_partner
          ? last_name_partner.length
            ? last_name_partner
            : undefined
          : undefined,
        birth_day: birth_day ? birth_day : undefined,
        birth_day_partner: birth_day_partner ? birth_day_partner : undefined,
      };

      const addressPayload = buildAddressPayload({
        privateAddress: private_address,
        companyAddress: company_address,
        preferredAddress,
      });

      if (addressPayload.requiresPreferredAddress) {
        setSnackbarMessage(t('retailer-nomination-choose-address'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      if (addressPayload.address) {
        payload.address = addressPayload.address;
        payload.preferred_address = addressPayload.preferredAddress;
        setPreferredAddress(addressPayload.preferredAddress);
      } else {
        delete payload.address;
      }

      try {
        const parsedPhone = parsePhoneNumberFromString(phone);

        if (!parsedPhone || !parsedPhone.isValid()) {
          setPhoneError(t('alert-phone-format'));
          setFocus('phone');
          return;
        } else {
          setPhoneError('');

          payload.prefix_phone = `+${parsedPhone.countryCallingCode}`;
          payload.phone = parsedPhone.nationalNumber;
          delete payload.sv_account_id;

          await updateHac(token, hacId, JSON.parse(JSON.stringify(payload)));

          setSnackbarMessage(t('alert-data-saved'));
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }

      } catch (error) {
        if (error.response) {
          if (error.response.status === 413) {
            setSnackbarOpen(true);
            setSnackbarSeverity('error');
            setSnackbarMessage(t('alert-address-too-large'));
          } else if (error.response.status === 409){
            setSnackbarOpen(true);
            setSnackbarSeverity('error');
            setSnackbarMessage(t('alert-duplicate'), error);
          } else {
            setSnackbarOpen(true);
            setSnackbarSeverity('error');
            setSnackbarMessage(t('common-internal-server-error'), error);
          }
        } else {
          setSnackbarOpen(true);
          setSnackbarSeverity('error');
          setSnackbarMessage(error);
        }
      }
    }
  );

  const validateAddressField = (field, allFields) => {
    const privateAddress = allFields.private_address;
    const isPrivateAddressSelected = preferredAddress === 'private';

    const isAnyFieldFilled = hasAnyAddressFieldFilled(privateAddress);

    if (isAnyFieldFilled || isPrivateAddressSelected) {
      if (!privateAddress[field]) {
        return t('alert-address-must-complete');
      }
    }
    return true;
  };

  const validateCompanyField = (field, allFields) => {
    const companyAddress = allFields.company_address;
    const isCompanyAddressSelected = preferredAddress === 'company';

    const isAnyFieldFilled = hasAnyAddressFieldFilled(companyAddress);

    if (isAnyFieldFilled || isCompanyAddressSelected) {
      if (!companyAddress[field]) {
        return t('alert-address-must-complete');
      }
    }
    return true;
  };

  const handleSaveSvId = async (e) => {
    e.preventDefault();

    // Si no es modificable, no hacer nada
    if (hacData?.is_modificable_sv_account === false) return;

    let valueToSend = '';

    if (svState === 'not_searched') {
      valueToSend = '';
    } else if (svState === 'id_not_found') {
      valueToSend = 'not_found';
    } else if (svState === 'id_added') {
      valueToSend = (svAccountId || '').trim();
    }

    try {
      setSvSaving(true);

      await updateSvId(token, hacId, { sv_account_id: valueToSend });

      setSnackbarMessage(t('alert-data-saved'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      setHacData((prev) => ({ ...prev, sv_account_id: valueToSend || null }));

      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      setSnackbarMessage(t('common-internal-server-error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSvSaving(false);
    }
  };

  useEffect(() => {
    // Si selecciona "no encontrado", guardamos el valor real para enviar
    if (svState === 'id_not_found') {
      setSvAccountId('not_found');
      return;
    }

    // Si vuelve a "no buscado", limpiamos
    if (svState === 'not_searched') {
      setSvAccountId('');
      return;
    }

    // Si pasa a "id_added" y antes tenÃ­amos not_found, dejamos el input limpio
    if (svState === 'id_added' && svAccountId === 'not_found') {
      setSvAccountId('');
    }
  }, [svState]); // eslint-disable-line react-hooks/exhaustive-deps



  return (
    <>
      {token && canAccessHacManagement(role) ? (
        <div className="App ribbon-wide">
          <Header/>
          <main>
            <RoleRibbonByRole role={role} navigate={navigate} translate={t} />
            <section
              className={`profile ${canAccessHacManagement(role) ? 'no-bg' : ''}`}>

              <div
                className="inner menu">

                <HacNavigation  />

                <main className="profile">
                  <form onSubmit={onSubmit}>
                    <BackButton
                      fallbackTo={location.state?.returnTo || queryReturnTo || backPath || getHacBackPathForRole(role, {
                        retailerStatus: profileStatus,
                        pmaOrigin: origin,
                      })}
                      context={breadcrumbName}
                    />


                    {trackingLink !== null && canShowTrackingLink && (
                        <p className="highlight">
                          <SafeHtml html={trackingText} />
                          {/*The Welcome Pack has already been sent. To track the status of the shipment, <a
                          href={trackingLink} target="_blank" rel="noreferrer">click here</a>*/}
                        </p>
                      )
                    }

                    <h2>{t('pma-profile-title')}</h2>


                    {profileStatus === HAC_STATUS.Active ||
                    !isRetailer ||
                    (profileStatus !== HAC_STATUS.Nominated &&
                      profileStatus !== HAC_STATUS.Exited) ? (
                      <div
                        className="title"
                        onClick={() => toggleSection('basic')}
                      >
                        <h3>{t('common-basic-info')}</h3>

                        <span className="btn text">
                        {expandedSections.basic ? (
                          <span className="icon-minus"></span>
                        ) : (
                          <span className="icon-plus"></span>
                        )}
                      </span>
                      </div>
                    ) : (
                      <div className="title">
                        <h3>{t('common-basic-info')}</h3>
                      </div>
                    )}
                    <Collapse in={expandedSections.basic}>
                      
                      <Grid container spacing={3}>
                        <Grid size={{xs: 12, sm: 12, md: 12}}>
                          {isPma && (
                            <div className={ (hacData?.is_modificable_sv_account === false && hacData?.sv_account_id !== null)  ? 'sv-id fixed' : 'sv-id' } >
                              <strong>SV-CRM ID</strong>
                              <Grid container spacing={3}>
                                <Grid size={{xs: 12, sm: 6, md: 8}} className="select">
                                  <div className="form-control required mui-select">
                                    <label htmlFor="sv-state">{t('common-sv-state')}</label>
                                    <FormControl fullWidth>
                                      <Select
                                        value={svState}
                                        onChange={(e) => setSvState(e.target.value)}
                                        disabled={hacData?.is_modificable_sv_account === false}
                                      >
                                        <MenuItem value="not_searched" disabled={hacData?.sv_account_id !== ""}>
                                          <div>{t('common-sv-not-search')}</div>
                                        </MenuItem>

                                        <MenuItem value="id_added">
                                          <div>{t('common-sv-added')}</div>
                                        </MenuItem>

                                        <MenuItem value="id_not_found">
                                          <div>{t('common-sv-not-found')}</div>
                                        </MenuItem>
                                      </Select>

                                    </FormControl>
                                  </div>
                                </Grid>
                                {(svState === 'id_added' || (svState === 'id_not_found' && hacData?.is_modificable_sv_account === false)) && (
                                  <Grid size={{xs: 12, sm: 6, md: 4}} className="input">
                                    <div className="form-control required">
                                      <Input
                                        type="text"
                                        id="sv_account_id"
                                        label={t('common-sv-id')}
                                        value={svState === 'id_not_found' ? t('common-sv-not-found') : svAccountId}
                                        onChange={(e) => setSvAccountId(e.target.value)}
                                        disabled={
                                          hacData?.is_modificable_sv_account === false ||
                                          svState === 'id_not_found'
                                        }
                                      />
                                    </div>
                                  </Grid>
                                )}
                              </Grid>
                              <p>{t('common-sv-warning')}</p>
                              <div className="actions">
                                <button
                                  type="button"
                                  className="btn primary fit"
                                  onClick={handleSaveSvId}
                                  disabled={hacData?.is_modificable_sv_account === false || svSaving}
                                >
                                  {t('common-save')}
                                </button>
                              </div>
                            </div>
                          )}



                        </Grid>
                      </Grid>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                          <div className="form-control required">
                            <label htmlFor="preferred_language">
                              {t('common-language')}
                            </label>
                            <select
                              id="preferred_language"
                              className="normal"
                              {...register('preferred_language')}
                              value={selectedLanguage || ''}
                            >
                              <option value="">{t('common-select-language')}</option>
                              {languages.map((language) => (
                                <option key={language} value={language}>
                                  {t(`languages-${language}`)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </Grid>
                      </Grid>
                      <Grid container spacing={3}>                          
                          <Grid size={{xs: 6, sm: 4, md: 2}}>
                              <div className="form-control">
                                <label htmlFor="title">{t('common-title')}</label>
                                <select
                                  name="title"
                                  id="title"
                                  className="normal"
                                  disabled={
                                    profileStatus === HAC_STATUS.Exited ||
                                    profileStatus === HAC_STATUS.Nominated
                                  }
                                  {...register('title')}
                                >
                                  <option value="Mr">{t('common-title-Mr')}</option>
                                  <option value="Mrs">{t('common-title-Mrs')}</option>
                                  <option value="Mx">{t('common-title-Mx')}</option>
                                </select>
                              </div>
                            </Grid>
                        {academicTitles.length !== 0 && (
                          <Grid size={{xs: 6, sm: 4, md: 4}}>
                            <div className="form-control">
                              <label htmlFor="academic-title">
                                {t('common-academic-title')}
                              </label>
                              <select
                                name="academic-title"
                                id="academic-title"
                                className="normal"
                                {...register('academic_title')}
                                disabled={
                                  profileStatus === HAC_STATUS.Exited ||
                                  profileStatus === HAC_STATUS.Nominated
                                }
                              >
                                <option key="" value="">
                                  {t('common-select-academic-title')}
                                </option>
                                {Array.isArray(academicTitles) &&
                                  academicTitles.map((academicTitle) => (
                                    <option
                                      key={academicTitle.sv_value}
                                      value={academicTitle.sv_value}
                                    >
                                      {academicTitle.text}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </Grid>
                        )}
                        <Grid size={{xs: 12, sm: 8, md: 5}}>
                          <div className="form-control required">
                            <Input
                              type="text"
                              id="name"
                              label={t('common-first-name')}
                              maxLength="50"
                              error={errors?.name}
                              disabled={
                                profileStatus === HAC_STATUS.Exited ||
                                profileStatus === HAC_STATUS.Nominated
                              }
                              {...register('name', {
                                validate: (v) => {
                                  const {error} = Joi.string()
                                    .required()
                                    .error(() => new Error(t('alert-name')))
                                    .validate(v);
                                  return error?.message ? error.message : true;
                                },
                              })}
                            />
                          </div>
                        </Grid>
                      </Grid>
                      <Grid container spacing={3}>
                        <Grid size={{xs: 12, sm: 4, md: 4}}>
                          <div className="form-control">
                            <label htmlFor="middle_name">
                              {t('common-middle-name')}
                            </label>
                            <input
                              maxLength="50"
                              type="text"
                              id="middle_mame"
                              disabled={
                                profileStatus === HAC_STATUS.Exited ||
                                profileStatus === HAC_STATUS.Nominated
                              }
                              {...register('middle_name', {})}
                            />
                          </div>
                        </Grid>
                        <Grid size={{xs: 12, sm: 5, md: 4}}>
                          <div className="form-control required">
                            <Input
                              maxLength="50"
                              type="text"
                              id="last_name"
                              label={t('common-last-name')}
                              error={errors?.last_name}
                              disabled={
                                profileStatus === HAC_STATUS.Exited ||
                                profileStatus === HAC_STATUS.Nominated
                              }
                              {...register('last_name', {
                                validate: (v) => {
                                  const {error} = Joi.string()
                                    .required()
                                    .error(() => new Error(t('alert-last-name')))
                                    .validate(v);
                                  return error?.message ? error.message : true;
                                },
                              })}
                            />
                          </div>
                        </Grid>
                        {profileStatus !== HAC_STATUS.Nominated && (
                          <Grid size={{xs: 12, sm: 3, md: 4}}>
                            <div className="form-control datepicker">
                              <label htmlFor="birth_day">
                                {t('common-birthday')}
                              </label>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Controller
                                  name="birth_day"
                                  control={control}
                                  render={({field}) => (
                                    <DateField
                                      {...field}
                                      format="DD/MM"
                                      disabled={
                                        profileStatus === HAC_STATUS.Exited
                                      }
                                      onChange={(date) => {
                                        const isValidDate = dayjs(date).isValid();
                                        if (isValidDate) {
                                          const formattedDate =
                                            dayjs(date).format('MM/DD');
                                          field.onChange(formattedDate);
                                        } else {
                                          field.onChange('');
                                        }
                                      }}
                                      value={
                                        field.value
                                          ? dayjs(field.value, 'MM/DD')
                                          : null
                                      }
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </div>
                          </Grid>

                        )}
                      </Grid>
                      <Grid container spacing={3}>
                        <Grid size={{xs: 12, sm: 6, md: 6}}>
                          <div className="form-control">
                            <label htmlFor="email">{t('common-email')}</label>
                            <input
                              maxLength="50"
                              type="text"
                              id="email"
                              {...register('email', {
                                validate: (v) =>
                                  Joi.string()
                                    .email({tlds: {allow: false}})
                                    .required()
                                    .validate(v),
                              })}

                            />
                          </div>
                        </Grid>
                        <Grid size={{xs: 12, sm: 12, md: 5}}>
                          <div className="form-control required">
                            <label htmlFor="phone">{t('common-phone')}</label>
                            <PhoneInput
                              value={phone}
                              onChange={(value) => {
                                setPhone(value);
                                setPhoneError('');
                              }}
                              onBlur={() => {
                                const parsed = parsePhoneNumberFromString(phone);
                                if (!parsed || !parsed.isValid()) {
                                  setPhoneError(t('alert-phone-format'));
                                } else {
                                  setPhoneError('');
                                }
                              }}
                              disabled={
                                profileStatus === HAC_STATUS.Exited || profileStatus === HAC_STATUS.Nominated
                              }
                            />
                            {phoneError && <p className="error-message">{phoneError}</p>}
                          </div>
                        </Grid>
                      </Grid>                    
                    
                      {profileStatus === HAC_STATUS.Nominated && canResendNomination && (
                          <div className="resend">
                            <h3>{t('retailer-nomination-resend-title')}</h3>
                            <p>{t('retailer-nomination-resend-text')}</p>
                            <button
                              className="btn secondary fit"
                              onClick={(event) => handleOpenResendModal(event)}
                            >
                              {t('retailer-nomination-resend-action')}
                            </button>
                          </div>
                        )}

                      {profileStatus === HAC_STATUS.Active && (
                        <Grid container spacing={3}>
                          <Grid size={{xs: 12, sm: 4, md: 4}}>
                            <div className="form-control">
                              <label htmlFor="name_partner">
                                {t('common-name-partner')}
                              </label>
                              <input
                                maxLength="50"
                                type="text"
                                id="name_partner"
                                disabled={
                                  profileStatus === HAC_STATUS.Exited ||
                                  profileStatus === HAC_STATUS.Nominated
                                }
                                {...register('name_partner', {})}
                              />
                            </div>
                          </Grid>
                          <Grid size={{xs: 12, sm: 5, md: 4}}>
                            <div className="form-control">
                              <label htmlFor="last_name_partner">
                                {t('common-last-name-partner')}
                              </label>
                              <input
                                maxLength="50"
                                type="text"
                                id="last_name_partner"
                                disabled={
                                  profileStatus === HAC_STATUS.Exited ||
                                  profileStatus === HAC_STATUS.Nominated
                                }
                                {...register('last_name_partner', {})}
                              />
                            </div>
                          </Grid>
                          <Grid size={{xs: 12, sm: 3, md: 4}}>
                            <div className="form-control datepicker">
                              <label htmlFor="birth_day_partner">
                                {t('common-birthday-partner')}
                              </label>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Controller
                                  name="birth_day_partner"
                                  control={control}
                                  render={({field}) => (
                                    <DateField
                                      {...field}
                                      format="DD/MM"
                                      disabled={
                                        profileStatus === HAC_STATUS.Exited ||
                                        profileStatus === HAC_STATUS.Nominated
                                      }
                                      onChange={(date) => {
                                        const isValidDate = dayjs(date).isValid();
                                        if (isValidDate) {
                                          const formattedDate =
                                            dayjs(date).format('MM/DD');
                                          field.onChange(formattedDate);
                                        } else {
                                          field.onChange('');
                                        }
                                      }}
                                      value={
                                        field.value
                                          ? dayjs(field.value, 'MM/DD')
                                          : null
                                      }
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </div>
                          </Grid>
                        </Grid>
                      )}
                      {isPma && (
                        <Grid container spacing={3}>
                          <Grid size={{xs: 12, sm: 12, md: 12}}>
                            <h3>{t('pma-profile-personal-notes-title')}</h3>
                            <p>{t('pma-profile-personal-notes-text')}</p>
                            <textarea
                              id="note"
                              maxLength="20000"
                              disabled={profileStatus === HAC_STATUS.Exited}
                              {...register('pma_notes')}
                              className="txt-area"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Collapse>
                    <br/>
                    <br/>

                    {((profileStatus === HAC_STATUS.Nominated && privateAddress) ||
                      profileStatus === HAC_STATUS.Active) && (
                      <>
                        <div
                          className="title"
                          onClick={() => toggleSection('address')}
                        >
                          <h3>{t('common-address-information')}</h3>
                          <span className="btn text">
                          {expandedSections.address ? (
                            <span className="icon-minus"></span>
                          ) : (
                            <span className="icon-plus"></span>
                          )}
                        </span>
                        </div>
                        <Collapse in={expandedSections.address}>
                          <HacAddressSection
                            t={t}
                            countries={countries}
                            errors={errors}
                            register={register}
                            privateAddress={privateAddress}
                            companyAddress={companyAddress}
                            preferredAddress={preferredAddress}
                            profileStatus={profileStatus}
                            chooseAddressTextKey="retailer-nomination-choose-address"
                            showWhenNominated
                            disableWhenReadOnly
                            privateAliasPlaceholder={t('nominate-alias-placeholder')}
                            onPreferredAddressChange={handlePreferredAddressChange}
                            onClearAddress={handleClearAddress}
                            validatePrivateField={validateAddressField}
                            validateCompanyField={validateCompanyField}
                          />
                        </Collapse>
                      </>
                    )}
                    {profileStatus !== HAC_STATUS.Exited && (
                        <Grid container spacing={3}>
                          <Grid size={{xs: 12, sm: 6, md: 6}}>
                            <button className="btn" type="submit">
                              {t('common-save')}
                            </button>
                          </Grid>
                        </Grid>
                      )}
                  </form>
                </main>
              </div>
            </section>
          </main>

          <ModalResendNomination
            open={resendModalOpen}
            handleClose={handleCloseResendModal}
            hacData={hacData}
          />
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
          >
            <Alert severity={snackbarSeverity} sx={{width: '100%'}}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </div>
      ) : (
        <Navigate to="/login"/>
      )}
    </>
  );
}

export default EditHacProfile;
